<script lang="ts">
export default {
  name: 'CompaniesAnalysisSearchQueries'
}
</script>

<script lang="ts" setup>
import {computed, reactive, ref, watch} from 'vue'
import { calcNotAvailableType } from 'src/biz_spa/domains/analysis/functions'
import { searchQueriesCrossAnalysisTypes, searchQueriesDataType } from 'src/biz_spa/domains/analysis/consts'
import { KkSearchQueryTable } from 'src/biz_spa/domains/analysis'
import { useSearchQueries } from 'src/biz_spa/domains/analysis/composables'
import type { SearchKeywordImpressionsPlotsParams, SearchKeywordImpressionsAggregatesParams } from 'src/biz_spa/apiClients'
import type { Sort } from 'src/biz_spa/domains/analysis/components/KkSearchQueryTable/index.vue'
import CompaniesAnalysisLayout from '../../../layouts/CompaniesAnalysisLayout.vue'
import {useStore} from 'vuex'
import { useSearchKeywordImpressionLabels } from 'src/biz_spa/domains/management/composables'
import type { Kind } from 'src/biz_spa/types'
import {useFilterModal} from 'src/biz_spa/composables'
import type {ChartData, ChartOptions} from 'chart.js'
import {colors, generateGraphId} from 'src/biz_spa/modules/analysis'
import moment from 'moment'
import {usePagination} from 'src/biz_spa/composables/usePagination'
import {useI18n} from 'vue-i18n'
import { KkLoading, KkSelect } from 'src/biz_spa/components/base'
import { KkFilterButton, KkPagination, KkInfo, KkTermSelect } from 'src/biz_spa/components/ui'
import { KkFilterModal } from 'src/biz_spa/components/projects'
import ChartLine from '../../../components/ChartLine.vue'
import Icon from '../../../components/Icon.vue'
import { actionEvent } from '../../../modules/googleAnalytics'
import type { TermSelectValue, ComparisonOption } from 'src/biz_spa/components/ui/KkTermSelect/types'
import type { ViewType } from 'src/biz_spa/domains/analysis/types'

type FormKind = Extract<
  Kind,
  | 'brands'
  | 'franchisees'
  | 'areas'
  | 'user_companies'
  | 'prefectures'
  | 'original_tag_group_a'
  | 'original_tag_group_b'
  | 'original_tag_group_c'
  | 'original_tag_group_d'
  | 'original_tag_group_e'
  | 'search_keyword_label_brandeds'
  | 'search_keyword_label_selecteds'
  | 'stores'
  | 'locales'
>

type FormValueArgs =
  | { kind: Extract<FormKind, 'stores' | 'locales' | 'search_keyword_label_brandeds' | 'search_keyword_label_selecteds'>, id: string }
  | { kind: Exclude<FormKind, 'stores' | 'locales' | 'search_keyword_label_brandeds' | 'search_keyword_label_selecteds'>, id: number }

const { t } = useI18n()
const store = useStore()

const {
  aggregates,
  plots,
  filteredAggregatesCount,
  all,
  totalCount,
  companyUuid,
  fetchAggregates,
  fetchComparedAggregates,
  fetchAggregatesCount,
  fetchPlots,
  resetPlots,
  isPlotsLoading,
  isAggregatesLoading,
  resetCompared,
} = useSearchQueries()

const readPlots = async (isCompared: boolean, graphIds = displayGraphs) => {
  await Promise.all(graphIds.map((graphId) => {
    return fetchPlots(getPlotsQuery(graphId, isCompared), isCompared)
  }))
}

// グラフデータの読み込み
const readGraph = async () => {
  resetPlots()
  await readPlots(false)
}

// テーブルデータの読み込み
const readAggregates = async () => {
  await fetchAggregates(getAggregatesQuery())
}

// 「全て」のみ表示するようグラフデータをリセット
const resetGraph = async () => {
  resetPlots()
  changeDisplayGraphs([generateGraphId(all.value)])
  await readPlots(false)
}

// テーブルデータとグラフを読み込む
const readAggregatesAndGraph = async () => {
  await readAggregates()
  await readGraph()
}

// テーブルデータを読み込み、グラフは初期化状態にする
const readAggregatesAndResetGraph = async () => {
  await readAggregates()
  await resetGraph()
}

const inboundOption = store.getters['companiesCompany/inboundOption']

const showOnlyOwnerAuthority = computed<boolean>(() => store.getters['companiesAccessibleApi/showOnlyOwnerAuthority'])

const term = ref<TermSelectValue>({
  start: moment().subtract(3, 'month').startOf('month'),
  end: moment().subtract(1, 'month').endOf('month'),
})

const compareTerm = ref<TermSelectValue>({
  start: null,
  end: null,
})

const termMin = computed(() => moment().subtract(3, 'year').subtract(6, 'month'))
const termMax = computed(() => moment().subtract(1, 'month').endOf('month'))

const compareOptions = computed<ComparisonOption[]>(() => {
  if (!term.value.start || !term.value.end) return []
  const monthsDiff = term.value.end.diff(term.value.start, 'months')
  const beforeTermEnd = moment(term.value.start).subtract(1, 'month').endOf('month')
  const beforeTermStart = moment(beforeTermEnd).subtract(monthsDiff, 'month').startOf('month')
  return [
    {
      key: 'before',
      name: t('pages.companies.analysis.searchQueries.comparison.previousPeriod'),
      value: {
        start: beforeTermStart,
        end: beforeTermEnd,
      },
      disabled: beforeTermStart.isBefore(termMin.value),
    },
    {
      key: 'lastYear',
      name: t('pages.companies.analysis.searchQueries.comparison.samePeriodLastYear'),
      value: {
        start: moment(term.value.start).subtract(1, 'year'),
        end: moment(term.value.end).subtract(1, 'year'),
      },
      disabled: moment(term.value.start).subtract(1, 'year').isBefore(termMin.value),
    },
  ]
})

const {
  filterItems,
  filterFormValues,
  initFilterItems,
  resetFilterModal,
  toggleFormValue,
  removeKind,
} = useFilterModal({ kinds: [
  'locales',
  'brands',
  'franchisees',
  'areas',
  'user_companies',
  'search_keyword_label_brandeds',
  'search_keyword_label_selecteds',
  'prefectures',
  'original_tag_group_a',
  'original_tag_group_b',
  'original_tag_group_c',
  'original_tag_group_d',
  'original_tag_group_e',
  'stores',
]})
const {
  currentPage,
  displayNumber,
  sortKey,
  sortOrder,
  setCurrentPage,
  setDisplayNumber,
  setSortKey,
  setSortOrder,
} = usePagination<typeof searchQueriesDataType[number]['dataType']>()
const { readLabels, labels: searchKeywordImpressionLabels } = useSearchKeywordImpressionLabels()

const enableFilterItems = computed(() => {
  if (!inboundOption) {
    return filterItems.value.filter((item) => item.kind !== 'locales')
  }
  return filterItems.value
})

const viewType = ref<ViewType>('greaterThan15')
const viewTypes = {
  '検索数15件以上のワードを表示': 'greaterThan15',
  '検索数15件未満のワードを表示': 'equalToLessThan15',
}
const changeViewType = (nextViewType: ViewType) => {
  viewType.value = nextViewType
}
watch(viewType, () => {
  compareTerm.value.start = null
  compareTerm.value.end = null
  setCurrentPage(1)
  if (viewType.value === 'greaterThan15') {
    readAggregatesAndResetGraph()
  } else {
    readAggregates()
  }
})

const readSearchAttributes = (companyUuid: string): Promise<void> => store.dispatch('companiesSearchTagGroups/readSearchTagGroups', companyUuid)

const notAvailableType = computed<string | null>(() => calcNotAvailableType({ vuexStoreName: 'companiesAnalysisSearchQueries' }))
const isCompared = computed<boolean>(() => Boolean(compareTerm.value.start && compareTerm.value.end))

// MEMO: term.valueのstart、endはnullableだが基本的に空の値（偽値）になることはないはず
const startTimeQuery = computed(() => term.value.start ? moment(term.value.start).startOf('month') : moment())
const endTimeQuery = computed(() => term.value.end ? moment(term.value.end).endOf('month') : moment())

const getAggregatesQuery = (comparison = false): SearchKeywordImpressionsAggregatesParams => ({
  analysisAxis: crossSearchFields[0].id,
  locales: filterFormValues.value.locales,
  brandIds: filterFormValues.value.brands,
  franchiseeIds: filterFormValues.value.franchisees,
  areaIds: filterFormValues.value.areas,
  userCompanyIds: filterFormValues.value.user_companies,
  prefectures: filterFormValues.value.prefectures,
  originalTagGroupATagIds: filterFormValues.value.original_tag_group_a,
  originalTagGroupBTagIds: filterFormValues.value.original_tag_group_b,
  originalTagGroupCTagIds: filterFormValues.value.original_tag_group_c,
  originalTagGroupDTagIds: filterFormValues.value.original_tag_group_d,
  originalTagGroupETagIds: filterFormValues.value.original_tag_group_e,
  searchKeywordLabelBranded: filterFormValues.value.search_keyword_label_brandeds,
  searchKeywordLabelSelected: filterFormValues.value.search_keyword_label_selecteds,
  storeUuids: filterFormValues.value.stores,
  startTime: comparison && compareTerm.value.start ? compareTerm.value.start : startTimeQuery.value,
  endTime: comparison && compareTerm.value.end ? compareTerm.value.end : endTimeQuery.value,
  sortKey: sortKey.value,
  sortOrder: sortOrder.value,
  page: currentPage.value,
  per: displayNumber.value,
  valueGteq: (viewType.value === 'greaterThan15') ? 15 : null,
  valueLteq: (viewType.value === 'equalToLessThan15') ? 14 : null,
})

const getPlotsQuery = (graphId: string, isCompared: boolean): SearchKeywordImpressionsPlotsParams => {
  const hyphenIndex = graphId.indexOf('-')
  const analysisAxisId = graphId.substring(hyphenIndex + 1)
  return {
    analysisAxis: crossSearchFields[0].id,
    analysisAxisId,
    locales: filterFormValues.value.locales,
    brandIds: filterFormValues.value.brands,
    franchiseeIds: filterFormValues.value.franchisees,
    areaIds: filterFormValues.value.areas,
    userCompanyIds: filterFormValues.value.user_companies,
    prefectures: filterFormValues.value.prefectures,
    originalTagGroupATagIds: filterFormValues.value.original_tag_group_a,
    originalTagGroupBTagIds: filterFormValues.value.original_tag_group_b,
    originalTagGroupCTagIds: filterFormValues.value.original_tag_group_c,
    originalTagGroupDTagIds: filterFormValues.value.original_tag_group_d,
    originalTagGroupETagIds: filterFormValues.value.original_tag_group_e,
    searchKeywordLabelBranded: filterFormValues.value.search_keyword_label_brandeds,
    searchKeywordLabelSelected: filterFormValues.value.search_keyword_label_selecteds,
    storeUuids: filterFormValues.value.stores,
    startTime: isCompared && compareTerm.value.start ? compareTerm.value.start.startOf('month') : moment(startTimeQuery.value),
    endTime: isCompared && compareTerm.value.end ? compareTerm.value.end.endOf('month') : moment(endTimeQuery.value),
  }
}

const totalPages = computed(() => Math.ceil(totalCount.value / displayNumber.value))
const goPrevPage = async () => {
  setCurrentPage(currentPage.value - 1)
  await readAggregates()

  if (isCompared.value) {
    await fetchComparedAggregates(getAggregatesQuery(true))
  }
}
const goNextPage = async () => {
  setCurrentPage(currentPage.value + 1)
  await readAggregates()

  if (isCompared.value) {
    await fetchComparedAggregates(getAggregatesQuery(true))
  }
}
const changeDisplayNumber = (event: Event) => {
  setDisplayNumber(Number((event.target as HTMLSelectElement).value))
  readAggregates()
}

const showFilterModal = ref<boolean>(false)
const handleFilterOpen = () => {
  showFilterModal.value = true
  fetchAggregatesCount(getAggregatesQuery())
}
const handleSubmit = async () => {
  setCurrentPage(1)
  showFilterModal.value = false
  await readAggregatesAndResetGraph()

  if (isCompared.value) {
    await fetchComparedAggregates(getAggregatesQuery(true))
    readPlots(true)
  }

  actionEvent('SearchFilter', JSON.stringify(getAggregatesQuery()))
}
const handleReset = async () => {
  showFilterModal.value = false
  resetFilterModal()

  setCurrentPage(1)
  await readAggregatesAndResetGraph()

  if (isCompared.value) {
    await fetchComparedAggregates(getAggregatesQuery(true))
    readPlots(true)
  }
}
const handleChangeFilterModalCheckBox = ({ kind, id }: FormValueArgs): void => {
  if (kind === 'stores' || kind === 'locales' || kind === 'search_keyword_label_brandeds' || kind === 'search_keyword_label_selecteds') {
    toggleFormValue({kind, id})
  } else {
    toggleFormValue({kind, id: id as number})
  }
  fetchAggregatesCount(getAggregatesQuery())
}
const handleCloseFilterModal = () => {
  showFilterModal.value = false
}
const crossSearchFields: typeof searchQueriesCrossAnalysisTypes = reactive([
  ...searchQueriesCrossAnalysisTypes
])
const dataTypes = computed(() => {
  if (!inboundOption) return searchQueriesDataType.filter((dataType) => dataType.id !== 'locale')
  return searchQueriesDataType
})

const getColor = (index: number ) => {
  // グラフを比較している場合は、比較前と比較後で同じ色にする
  const colorIndex = isCompared.value ? Math.floor(index / 2) : index
  return colors[colorIndex % 10]
}

const chartData = computed<ChartData | null>(() => {
  const data = plots.value.filter(plot => displayGraphs.includes(generateGraphId(plot)))
  if (data.length === 0) { return null }

  const labels = Object.keys(plots.value[0].impressionCount).map(v => moment(v).format('Y年M月'))

  return {
    labels,
    datasets: data.map((plot, index) => ({
      label: plot.fields.map(v => v.name).join(' / '),
      data: Object.values(plot.impressionCount),
      backgroundColor: ['rgba(255, 255, 255, 0)'],
      borderColor: [getColor(index)],
      borderWidth: 2,
      pointBorderColor: getColor(index),
      lineTension: 0,
      pointRadius: 4,
      borderDash: plot.isCompared ? [5, 5] : [],
      labels: Object.keys(plot.impressionCount).map((v) => moment(v).format('Y年M月')),
    }))
  }
})

const chartOptions = computed<ChartOptions>(() => {
  const y = { beginAtZero: true }
  const x = {}
  return {
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: 4,
    scales: {
      y,
      x
    },
    plugins: {
      tooltip: {
        callbacks: {
          title: (args) => {
            // FIXME: 正しい日付を保持するための暫定対応
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return (args[0].dataset as any).labels[args[0].dataIndex]
          },
        },
      }
    },
  }
})

const loading = computed<boolean>(() => isAggregatesLoading.value || isPlotsLoading.value )

watch(term, async (newValue) => {
  setCurrentPage(1)

  await readAggregatesAndGraph()
  if (isCompared.value) {
    await fetchComparedAggregates(getAggregatesQuery(true))
    readPlots(true)
  }

  if (newValue.start && newValue.end) {
    actionEvent('TimeFilter_search', JSON.stringify({
      start_term: newValue.start.format('YYYY-MM-DD'),
      end_term: newValue.end.format('YYYY-MM-DD'),
    }))
  }
})

watch(compareTerm, async (newValue) => {
  if (!newValue.start || !newValue.end) {
    resetCompared()
    return
  }
  await fetchComparedAggregates(getAggregatesQuery(true))
  readPlots(true)

  actionEvent('TimeFilter_search', JSON.stringify({
    start_compare: newValue.start.format('YYYY-MM-DD'),
    end_compare: newValue.end.format('YYYY-MM-DD'),
  }))
})

const load = async () => {
  await readLabels()
  await readSearchAttributes(companyUuid.value)
  resetFilterModal()
  initFilterItems()
  readAggregatesAndResetGraph()

  // ラベルに関する絞り込みはキーワードが設定されている場合のみ表示
  if (searchKeywordImpressionLabels.value) {
    for (const label of searchKeywordImpressionLabels.value) {
      switch (label.id) {
      case 'branded':
        if (!label.keywords.length) removeKind('search_keyword_label_brandeds')
        break
      case 'selected':
        if (!label.keywords.length) removeKind('search_keyword_label_selecteds')
        break
      default:
        break
      }
    }
  }
}
load()

const sort = ref<Sort>({
  sortKey: '',
  sortOrder: '',
})
watch(sort, async (newValue) => {
  if (newValue.sortOrder === '') return
  const data = dataTypes.value.find(v => v.id === newValue.sortKey)
  if (data == null) throw new Error('存在しないソートキーが指定されました。')
  setSortKey(data.dataType)
  setSortOrder(newValue.sortOrder)
  setCurrentPage(1)
  await readAggregates()

  if (isCompared.value) {
    await fetchComparedAggregates(getAggregatesQuery(true))
  }
}, { deep: true })

const graph = ref<string[]>(['search_keyword-0'])
const displayGraphs = reactive<string[]>([])
const changeDisplayGraphs = (values: string[]) => {
  displayGraphs.length = 0
  values.forEach(v => {
    displayGraphs.push(v)
  })
}

watch(graph, async (newValue) => {
  // 新しく追加されたグラフIDの中でキャッシュがないもののみを抽出して、APIを叩く
  const values = newValue.filter(v => !displayGraphs.includes(v))
  changeDisplayGraphs(newValue)
  const notCachedGraphs = values.filter(graphId => {
    return !plots.value.map(v => generateGraphId(v)).includes(graphId)
  })
  await readPlots(false, notCachedGraphs)
  if (isCompared.value) {
    await readPlots(true, notCachedGraphs)
  }
})
</script>

<template>
  <CompaniesAnalysisLayout
    :not-available-type="notAvailableType"
    :company-uuid="companyUuid"
  >
    <template #header>
      <div class="searchQueryHeader">
        <div class="searchQueryHeader__left">
          <div class="searchQueryHeader__viewType">
            <KkSelect
              :model-value="viewType"
              :options="viewTypes"
              :disabled="loading"
              @update:model-value="changeViewType"
            />
          </div>
          <div class="searchQueryHeader__filterButton">
            <KkFilterButton :disabled="loading" @click="handleFilterOpen" />
          </div>
        </div>
        <div class="searchQueryHeader__right">
          <KkTermSelect
            v-model="compareTerm"
            :min="termMin"
            :max="termMax"
            :comparison-options="compareOptions"
            :disabled="viewType === 'equalToLessThan15'"
            type="month"
            comparison
          />
          <KkTermSelect v-model="term" :min="termMin" :max="termMax" type="month" />
        </div>
      </div>
    </template>
    <template #overview>
      <div class="p-box__info">
        <div class="c-info">
          <div class="c-info__icon">
            <Icon name="iconQuestion" />
          </div>
          <div class="c-info__content">
            <p>{{ t('pages.companies.analysis.searchQueries.info') }}</p>
          </div>
        </div>
      </div>
    </template>
    <template v-if="viewType === 'greaterThan15'" #graph>
      <div v-if="chartData">
        <ChartLine
          :chart-data="chartData"
          :options="chartOptions"
          :height="360"
        />
      </div>
      <KkLoading v-if="isPlotsLoading" />
    </template>
    <template #table>
      <KkInfo v-if="showOnlyOwnerAuthority" icon="hint" class="moreInfo">
        {{ t('pages.companies.analysis.searchQueries.labelInfo.part1') }}
        <router-link :to="{ name: 'CompaniesSearchKeywordImpressionLabels', params: { company_uuid: companyUuid } }" class="keywordSettingLink">
          {{ t('pages.companies.analysis.searchQueries.labelInfo.part2') }}
        </router-link>
      </KkInfo>
      <KkSearchQueryTable
        v-model:sort="sort"
        v-model:graph="graph"
        :term="`${term.start?.format('YYYY/MM/DD')}-${term.end?.format('YYYY/MM/DD')}`"
        :comparison-term="`${compareTerm.start?.format('YYYY/MM/DD')}-${compareTerm.end?.format('YYYY/MM/DD')}`"
        :all="all"
        :items="aggregates"
        :total-count="totalCount"
        :comparison="isCompared"
        :view-type="viewType"
        :loading="isAggregatesLoading"
      />
    </template>
    <template #paginator>
      <KkPagination
        :loading="isAggregatesLoading"
        :current-page="currentPage"
        :display-number="displayNumber"
        :contents-length="aggregates.length"
        :total-pages="totalPages"
        @onChangeDisplayNumber="changeDisplayNumber"
        @goPrevPage="goPrevPage"
        @goNextPage="goNextPage"
      />
    </template>
    <template #filter>
      <KkFilterModal
        :is-visible="showFilterModal"
        :filter-items="enableFilterItems"
        :form-values="filterFormValues"
        :total-count="filteredAggregatesCount"
        @close="handleCloseFilterModal"
        @submit="handleSubmit"
        @reset="handleReset"
        @change-check-box="handleChangeFilterModalCheckBox"
      />
    </template>
  </CompaniesAnalysisLayout>
</template>

<style lang="scss" scoped>
@import "../../../../../stylesheets/biz_spa/foundation/var";
@import "../../../../../stylesheets/biz_spa/foundation/mixin";
.searchQueryHeader {
  width: 100%;
  &__filterButton {
    margin-top: 16px;
  }
  &__right {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-top: 16px;
  }
  @include mq(md) {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 16px 24px;
    align-items: center;
    width: 100%;
    &__left {
      display: flex;
      align-items: center;
    }
    &__right {
      flex-direction: row;
      align-items: center;
      margin-top: 0;
    }
    &__filterButton {
      margin: 0 0 0 24px;
    }
  }
}
.keywordSettingLink {
  color: #E9446A;
}
.filterStoreSection {
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: 16px 32px;
  border: 1px solid #E4E7EB;
  border-radius: 4px;
  background-color: #F8F9FA;
  &__text {
    display: flex;
    align-items: center;
  }
  &__firstStoreName {
    margin-right: 8px;
  }
  &__heading {
    font-weight: bold;
  }
  &__storeCount {
    color: $color-accent-primary;
    text-decoration: underline;
  }
}
.c-info__icon {
  display: flex;
  align-items: center;
  padding: 0 8px 0 4px;
}
.moreInfo {
  margin-bottom: 24px;
}
</style>
